import React from 'react';

/**
 * Logos des messageries en monochrome (lucide n'a pas de logos de marque) :
 * formes reconnaissables, a la couleur du texte (encre cote createur), sans
 * reprendre les couleurs officielles (decision du 14/10 : pas de vert
 * WhatsApp). Les details interieurs sont en blanc, sur la pastille pleine.
 */
type GlyphProps = React.SVGProps<SVGSVGElement>;

function Glyph({ children, ...props }: GlyphProps & { children: React.ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden focusable="false" {...props}>
      {children}
    </svg>
  );
}

/** Bulle ronde au trait et combine de telephone plein. */
export function WhatsAppGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <path d="M3.6 20.4 4.9 16.3A8.4 8.4 0 1 1 8 19.2Z" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinejoin="round" />
      <path
        d="M9 8.2c.3-.5.6-.5.9-.5h.5c.2 0 .4.1.5.4l.7 1.6c.1.2 0 .5-.1.6l-.5.6c-.1.1-.2.3 0 .5.3.6 1.4 1.9 2.7 2.4.2.1.4 0 .5-.1l.6-.7c.2-.2.4-.2.6-.1l1.5.7c.3.1.4.3.4.5 0 .5-.2 1.3-1 1.7-.7.4-1.7.4-3.6-.5-2.3-1.1-3.7-3.4-3.9-3.7-.2-.3-.8-1.3-.8-2.4 0-.9.4-1.4.6-1.6z"
        fill="currentColor"
      />
    </Glyph>
  );
}

/** Pastille ronde et « f » en reserve. */
export function FacebookGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path d="M13.3 22v-7.6h2.3l.4-2.7h-2.7V10c0-.8.3-1.3 1.3-1.3h1.5V6.3c-.3 0-1.1-.1-2.1-.1-2.1 0-3.5 1.3-3.5 3.6v1.9H8.2v2.7h2.3V22z" fill="#FFFFFF" />
    </Glyph>
  );
}

/** Bulle pleine et eclair en zigzag. */
export function MessengerGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <path d="M12 2.5C6.6 2.5 2.5 6.4 2.5 11.5c0 2.7 1.1 5 3 6.6v3.4l3.2-1.8c1 .3 2.1.4 3.3.4 5.4 0 9.5-3.9 9.5-9S17.4 2.5 12 2.5z" fill="currentColor" />
      <path d="m6.5 14.4 3-4.7c.4-.6 1.2-.8 1.8-.3l2.4 1.8 3.8-2.6-3 4.7c-.4.6-1.2.8-1.8.3l-2.4-1.8z" fill="#FFFFFF" />
    </Glyph>
  );
}

/** Lettre X de la marque. */
export function XGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <path d="M17.8 3h3.1l-6.8 7.7L22 21h-6.2l-4.9-6.4L5.3 21H2.2l7.3-8.3L2 3h6.4l4.4 5.8zm-1.1 16.2h1.7L7.4 4.7H5.5z" fill="currentColor" />
    </Glyph>
  );
}

/** Pastille ronde et avion en papier. */
export function TelegramGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path d="m6.2 11.7 10.2-3.9c.5-.2.9.1.8.8l-1.7 8.1c-.1.6-.5.7-1 .4l-2.7-2-1.3 1.3c-.1.1-.3.2-.5.2l.2-2.8 5-4.6c.2-.2 0-.3-.3-.1l-6.2 3.9-2.7-.8c-.6-.2-.6-.6.2-.9z" fill="#FFFFFF" />
    </Glyph>
  );
}

/** Pastille ronde et « P » en reserve (partage des articles du blog). */
export function PinterestGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path
        d="M12.4 5.6c-3.6 0-5.4 2.6-5.4 4.7 0 1.3.5 2.4 1.5 2.8.2.1.3 0 .4-.2l.2-.6c0-.2 0-.3-.1-.5-.3-.4-.5-.9-.5-1.6 0-2 1.5-3.9 4-3.9 2.2 0 3.4 1.3 3.4 3.1 0 2.4-1 4.4-2.6 4.4-.9 0-1.5-.7-1.3-1.6.2-1 .7-2.2.7-2.9 0-.7-.4-1.2-1.1-1.2-.9 0-1.6.9-1.6 2.2 0 .8.3 1.3.3 1.3l-1.1 4.6c-.2.8-.1 1.9 0 2.7l.5.1c.4-.6 1.1-1.8 1.3-2.6l.5-1.8c.3.5 1 .9 1.8.9 2.4 0 4-2.2 4-5.1 0-2.2-1.9-4.3-4.8-4.3z"
        fill="#FFFFFF"
      />
    </Glyph>
  );
}

/** Carre arrondi plein et « in » en reserve. */
export function LinkedInGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <rect x="2.5" y="2.5" width="19" height="19" rx="3" fill="currentColor" />
      <path d="M7 10v7M7 7v.01M11 17v-4a2 2 0 0 1 4 0v4M11 10v7" stroke="#FFFFFF" strokeWidth={2} strokeLinecap="round" fill="none" />
    </Glyph>
  );
}
